import { format, parse } from "date-fns";
import { useNavigate } from "react-router";

import type { AssetBookingDetailDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { Carousel } from "@/components/Carousel/Carousel";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { Pdf } from "@/components/Pdf/Pdf";
import { timeWithoutSeconds } from "@/helpers/date";
import { useSlug } from "@/hooks/useSlug";
import { Specification } from "@/modules/bookings/components/Specification";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface LayoutProps {
  reservation: AssetBookingDetailDto;
  onDelete: () => Promise<void>;
}
export function Layout({ reservation, onDelete }: LayoutProps): React.ReactNode {
  const slug = useSlug();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-reservation-modal");
  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      callbackFn: () =>
        void navigate(
          routes.bookings.editBooking({
            slug,
            aid: reservation.asset.id,
            bid: reservation.id,
          }),
        ),
      isHidden: !reservation.canEdit || Boolean(reservation.cancelledAt),
    },
    {
      labels: {
        default: t("common.action.cancel"),
      },
      value: "cancel",
      icon: <Trash02Icon />,
      callbackFn: () => openDeleteModal(reservation.id),
      isHidden: !reservation.canDelete || Boolean(reservation.cancelledAt),
    },
  ];
  function getCancellationReason(): string {
    switch (reservation.cancellationReason) {
      case "cancelled":
        return reservation.cancellationReasonDescription ?? t("page.bookings.reservation-details.cancelled.cancelled");
      case "assetDeleted":
        return t("page.bookings.reservation-details.cancelled.deleted");
      case "assetUnpublished":
        return t("page.bookings.reservation-details.cancelled.unpublished");
      case "assetUpdated":
        return t("page.bookings.reservation-details.cancelled.updated");
      default:
        return "";
    }
  }

  return (
    <DocumentPaper
      title={t("page.bookings.reservation-details.title")}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.bookings.reservation-details.breadcrumbs.calendar.title"),
              to: `${routes.calendar.list({
                slug,
              })}?tab=reservations`,
            },
            {
              name: t("page.bookings.reservation-details.breadcrumbs.reservation.title"),
            },
          ]}
        />
      }
    >
      <PageGrid.Grid>
        <PageGrid.Item size="50%">
          <div className="h-fit w-full overflow-hidden rounded-lg">
            <div className="aspect-video w-full">
              <Carousel images={reservation.asset.images} allowZoom />
            </div>
            <div className="flex flex-col gap-4 rounded-b-lg bg-white p-4">
              {reservation.cancelledAt ? (
                <div className="flex justify-between">
                  <div className="flex flex-col gap-2">
                    <div className="w-fit rounded-lg bg-red-100 px-2 py-1">
                      <span className="text-caption-bold text-red-500">
                        {t("page.bookings.reservations.reservation-cancelled")}
                      </span>
                    </div>
                    <span className="text-body">{getCancellationReason()}</span>
                  </div>
                  <ContextMenu items={menuActions} />
                </div>
              ) : null}
              <div className="flex justify-between">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="text-grey-700" />
                  <span className="text-caption">{`${format(parse(reservation.date, "yyyy-mm-dd", new Date()), "dd")} ${formatDate(i18n, "month", parse(reservation.date, "yyyy-MM-dd", new Date()))} ${timeWithoutSeconds(reservation.startTime)} - ${timeWithoutSeconds(reservation.endTime)}`}</span>
                </div>
                {!reservation.cancelledAt ? <ContextMenu items={menuActions} /> : null}
              </div>
              <span className="text-headline3 leading-old-headline4">{reservation.asset.name}</span>
              <div className="flex items-center gap-4">
                <Specification specification="locationSpecification" value={reservation.asset.locationSpecification} />
              </div>
              {!reservation.asset.deletedAt ? (
                <Button
                  type="link"
                  styling="tertiary"
                  className="text-aop-basic-blue-500"
                  href={routes.bookings.bookAsset({
                    slug,
                    aid: reservation.asset.id,
                  })}
                >
                  {t("page.bookings.reservation-details.asset-details")}
                </Button>
              ) : null}
              {reservation.bookingReason ? (
                <div className="flex flex-col gap-2">
                  <span className="text-body-bold">{t("page.bookings.reservation-details.reason")}</span>
                  <span className="text-body">{reservation.bookingReason}</span>
                </div>
              ) : null}
              {reservation.regulationDocument ? (
                <div className="flex w-fit flex-col gap-2">
                  <span className="text-body-bold">{t("page.bookings.reservation-details.regulations")}</span>
                  <Pdf
                    fileName={reservation.regulationDocument?.fileName}
                    onClick={() => window.open(reservation.regulationDocument?.url, "_blank")}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </PageGrid.Item>
      </PageGrid.Grid>
      <DeleteModal
        title={t("page.bookings.reservation-details.delete.modal.title")}
        description={t("page.bookings.reservation-details.delete.modal.description")}
        onDelete={onDelete}
        rejectBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.keep-reservation"),
          "data-testid": "modal-cancel-delete",
        }}
        deleteBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.cancel-reservation"),
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPaper>
  );
}
